import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import { faAngleLeft, faDownload } from '@fortawesome/free-solid-svg-icons';
import { GoogleMap, Marker, Polygon } from '@react-google-maps/api';

//STYLES
import { OverlayVerMalha, ContainerVerMalhaModal, WrapperMapaVerMalha, CardFlutuanteInfo } from './styled';

//APP
import { calculoAreaCoord } from '../../../../../../../../../../../../services/Afins';
import { montarNomeArquivoKml } from '../../meta';

//COMPONENTS
import ButtonCustom from '../../../../../../../../../../../../Components/ButtonCustom';
import { Box } from '../../../../../../../../../../../../Components/CustomUi/box';
import { geralKmlPontos } from '../../../../../../../../../../../../Components/gerarKml';
import { LateralVerAnalises } from './lateralVerAnalises';

export interface PointCustom {
    INDICE: number;
    NOME: number | string;
    LATITUDE: number;
    LONGITUDE: number;
    COR?: string | null;
    id?: string | number;
    [key: string]: any;
}

interface MapModalProps {
    isOpen: boolean
    onClose: () => void
    talhaoGeoJson: any
    initialPoints: PointCustom[] | any
    analises: any[]
    selecionado: number | null
    setSelecionado: React.Dispatch<React.SetStateAction<number | null>>
    nomePropriedade?: string
    nomeTalhao?: string
}

const talhaoOptions = {
    fillColor: '#14b9c5',
    fillOpacity: 0.25,
    strokeColor: '#00e1ff',
    strokeOpacity: 1,
    strokeWeight: 2,
    clickable: false,
}

const OverlayMotion = motion(OverlayVerMalha)
const ContainerMotion = motion(ContainerVerMalhaModal)

export const ModalVerMalha: React.FC<MapModalProps> = ({
    isOpen,
    onClose,
    talhaoGeoJson,
    initialPoints,
    analises,
    selecionado,
    setSelecionado,
    nomePropriedade,
    nomeTalhao
}) => {

    const [points, setPoints] = useState<PointCustom[]>([])
    const masterPoints = useRef<PointCustom[]>([])

    const mapRef = useRef<google.maps.Map | null>(null)
    const fitBoundsDone = useRef(false)

    const atualizarCoresDosPontos = (todosOsPontos: PointCustom[], selecionados: any[], corDoGrupo: string) => {
        const coresMap = new Map(selecionados.map((p) => [String(p.INDICE || p.properties?.INDICE), corDoGrupo]))

        return todosOsPontos.map((ponto) => {
            const corSelecionada = coresMap.get(String(ponto.INDICE))

            if (corSelecionada) {
                return {
                    ...ponto,
                    COR: corSelecionada,
                }
            }

            return {
                ...ponto,
                COR: null,
            }
        })
    }

    const aoClicarNaAnalise = (analiseClicada: any) => {
        if (selecionado === analiseClicada.id) {
            setSelecionado(null)

            const pontosResetados = masterPoints.current.map((ponto) => ({
                ...ponto,
                COR: null,
            }))
            setPoints(pontosResetados)
        } else {
            setSelecionado(analiseClicada.id)

            const novosPontos = atualizarCoresDosPontos(
                masterPoints.current,
                analiseClicada.pontos_selecionados,
                analiseClicada.cor
            )
            setPoints(novosPontos)
        }
    }

    const handleBaixarKml = () => {
        if (points.length === 0) return

        // Nome do arquivo/documento = propriedade + talhão; cai no nome genérico se não houver.
        const nomeArquivo = montarNomeArquivoKml([nomePropriedade, nomeTalhao], 'malha_geral')
        const nomeDocumento = [nomePropriedade, nomeTalhao]
            .filter((parte) => (parte ?? '') !== '')
            .join(' - ') || 'Malha Geral'

        const kmlString = geralKmlPontos({ pontos: points }, nomeDocumento)
        const blob = new Blob([kmlString], { type: 'application/vnd.google-earth.kml+xml' })
        const url = URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = `${nomeArquivo}.kml`
        link.click()

        URL.revokeObjectURL(url)
    }

    useEffect(() => {
        if (initialPoints && Array.isArray(initialPoints)) {
            setPoints(initialPoints)
            masterPoints.current = initialPoints
        }
    }, [initialPoints])

    useEffect(() => {
        if (!isOpen) {
            fitBoundsDone.current = false
            setSelecionado(null)

            if (masterPoints.current.length > 0) {
                const pontosLimpos = masterPoints.current.map((p) => ({
                    ...p,
                    COR: null
                }))
                setPoints(pontosLimpos)
            }
        }
    }, [isOpen, JSON.stringify(talhaoGeoJson)])

    const defaultCenter = useMemo(() => ({ lat: -15, lng: -47 }), [])

    const talhaoPath = useMemo(() => {
        if (!talhaoGeoJson) return []
        return talhaoGeoJson
    }, [JSON.stringify(talhaoGeoJson)])

    const fitBoundsMap = (mapInstance: google.maps.Map) => {
        if (!mapInstance || fitBoundsDone.current) return

        if (talhaoPath.length > 0) {
            const bounds = new google.maps.LatLngBounds()
            talhaoPath.forEach((coord: any) => bounds.extend(coord))
            mapInstance.fitBounds(bounds)
            fitBoundsDone.current = true
        } else if (points.length > 0) {
            const bounds = new google.maps.LatLngBounds()
            let hasValidPoint = false
            points.forEach((pt) => {
                const latVal = Number(pt.LATITUDE)
                const lngVal = Number(pt.LONGITUDE)

                if (!isNaN(latVal) && !isNaN(lngVal) && latVal !== 0 && lngVal !== 0) {
                    bounds.extend({ lat: latVal, lng: lngVal })
                    hasValidPoint = true
                }
            })

            if (hasValidPoint && !bounds.isEmpty()) {
                mapInstance.fitBounds(bounds)
                fitBoundsDone.current = true
            }
        }
    }

    useEffect(() => {
        if (isOpen && mapRef.current && !fitBoundsDone.current) {
            const timer = setTimeout(() => {
                fitBoundsMap(mapRef.current!)
            }, 300)
            return () => clearTimeout(timer)
        }
    }, [isOpen, talhaoPath, points])

    return (
        <AnimatePresence>
            {isOpen && (
                <OverlayMotion
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <ContainerMotion
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <WrapperMapaVerMalha>
                            <GoogleMap
                                mapContainerStyle={{ width: '100%', height: '100%' }}
                                zoom={14}
                                center={talhaoPath?.[0] || defaultCenter}
                                onLoad={(map) => {
                                    mapRef.current = map
                                    fitBoundsMap(map)
                                }}
                                options={{
                                    mapTypeId: 'satellite',
                                    streetViewControl: false,
                                    mapTypeControl: false,
                                    fullscreenControl: false,
                                    gestureHandling: 'greedy',
                                }}
                            >
                                {talhaoPath.length > 0 && <Polygon paths={talhaoPath} options={talhaoOptions} />}

                                {points.map((pt, index) => {
                                    const lng = Number(pt.LONGITUDE)
                                    const lat = Number(pt.LATITUDE)
                                    const labelText = pt.NOME ? pt.NOME.toString() : (index + 1).toString()
                                    const markerKey = `view-${pt.INDICE || index}-${lat}-${lng}`

                                    return (
                                        <Marker
                                            key={markerKey}
                                            position={{ lat, lng }}
                                            clickable={false}
                                            label={{
                                                text: labelText,
                                                color: pt.COR ? 'white' : '#7b7b7b',
                                                fontWeight: 'bold',
                                                fontSize: '10px',
                                            }}
                                            icon={{
                                                path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
                                                fillColor: pt.COR || '#ffffff',
                                                fillOpacity: 1,
                                                strokeWeight: 1,
                                                strokeColor: '#2c2c2c',
                                                scale: 1.5,
                                                anchor: new google.maps.Point(12, 22),
                                                labelOrigin: new google.maps.Point(12, 9),
                                            }}
                                        />
                                    )
                                })}
                            </GoogleMap>

                            {/* Card flutuante - Informações do talhão */}
                            <CardFlutuanteInfo>
                                <div>
                                    <ButtonCustom
                                        danger
                                        icon={<FontAwesomeIcon icon={faAngleLeft} />}
                                        onClick={onClose}
                                        type='tertiary'
                                    >
                                        Voltar
                                    </ButtonCustom>
                                </div>
                                <div className='container__info__malha'>
                                    <div className='info__item'>
                                        <label>Área do talhão</label>
                                        <p>{calculoAreaCoord([talhaoPath])} ha</p>
                                    </div>
                                    <div className='info__item'>
                                        <label>Total de pontos</label>
                                        <p>{points.length}</p>
                                    </div>
                                    <div className='info__item'>
                                        <label>Grid amostral</label>
                                        <p>
                                            {points.length > 0
                                                ? (points.length / calculoAreaCoord([talhaoPath])).toFixed(2)
                                                : 0}{' '}
                                            pontos/ha
                                        </p>
                                    </div>
                                </div>
                                <Box>
                                    <LateralVerAnalises
                                        analises={analises}
                                        atualizarCoresDosPontos={aoClicarNaAnalise}
                                        selecionado={selecionado}
                                        setSelecionado={setSelecionado}
                                    />
                                </Box>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <ButtonCustom
                                        type='secondary'
                                        onClick={handleBaixarKml}
                                        icon={<FontAwesomeIcon icon={faDownload} />}
                                        disabled={points.length === 0}
                                    >
                                        Baixar KML da malha geral
                                    </ButtonCustom>
                                </div>
                            </CardFlutuanteInfo>

                        </WrapperMapaVerMalha>
                    </ContainerMotion>
                </OverlayMotion>
            )}
        </AnimatePresence >
    )
}
